import { useEffect, useState } from "react";
import { t } from "i18next";
import { cn } from "@edusogno/lib/utils";
import { useMe } from "@/data/user";
import { Trans } from "react-i18next";
import { motion } from "framer-motion";
import { Progress } from "@edusogno/primitives/progress";
import { getMessage } from "@/utils/home";
import { MessageEnum } from "@/types";
import { fadeInStagger } from "@/utils/motion/fade-in-stagger";
import { INTERNAL_DATE_FORMAT } from "@/utils/agenda";
import Card from "./Card";
import Dash from "../icons/home/Dash";
import dayjs from "dayjs";
import alien from "@/assets/animations/alien.json";
import Lottie from "react-lottie-player";
import useGlobals from "@/utils/hooks/useGlobals";
import Notifications from "./notifications/Index";
import CourseStructure from "./course-structure/DesktopCourseStructure";
import useCourseDetails from "@/utils/hooks/useCourseDetails";
import AbsoluteBackground from "../ui/AbsoluteBackground";

const DesktopView = () => {
  const { me } = useMe();
  const animation = fadeInStagger();
  const { setGlobalState } = useGlobals();

  const {
    course,
    message,
    endDate,
    progress,
    startDate,
    isOneToOne,
    lessonsDone,
    exercisesDone,
    recentlyDoneExercises,
    percentageLessonsDone,
    percentageExecisesDone,
  } = useCourseDetails();

  const noPercentageLessonsDone = !(
    typeof percentageLessonsDone === "number" && isFinite(percentageLessonsDone)
  );

  const noPercentageExercisesDone = !(
    typeof percentageExecisesDone === "number" &&
    isFinite(percentageExecisesDone)
  );

  const noProgress = !(typeof progress === "number" && isFinite(progress));

  const [isOneToOneOnly, setIsOneToOneOnly] = useState(false);

  useEffect(() => {
    setGlobalState("containerize", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!me?.item.user) return;

    const userProducts = me.item.user.products ? me.item.user.products : [];

    let totalOneToOne = 0;
    let totalOtherPriorities = 0;

    userProducts.forEach((product) => {
      const courseStructureArray = product?.course_info?.course_structure;

      if (courseStructureArray && courseStructureArray.length > 0) {
        courseStructureArray.forEach((courseStructure) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { one_to_one, ...otherPriorities } = courseStructure;

          totalOneToOne += one_to_one;
          totalOtherPriorities += Object.values(otherPriorities)
            .filter((value): value is number => typeof value === "number")
            .reduce((sum, value) => sum + value, 0);
        });
      }
    });

    if (totalOneToOne > 0 && totalOtherPriorities === 0) {
      setIsOneToOneOnly(true);
    } else {
      setIsOneToOneOnly(false);
    }
  }, [me]);

  return (
    <>
      <AbsoluteBackground className="bg-gradient z-[9] w-full" />

      <div className="z-[10] 3xl:container 3xl:mx-auto h-full flex flex-col gap-3">
        <div className="flex-[1_1_110px] gap-3 justify-between w-full rounded-md bg-[#FFFFFFCC] flex flex-row items-center">
          <div className="flex w-[30%] items-center p-2 pr-0 flex-row gap-4 flex-[1_1_30%]">
            <Lottie
              loop
              play
              style={{ height: "110px", width: "auto" }}
              animationData={alien}
            />

            <p
              className={cn(
                "text-secondary w-[60%] text-sm 3xl:text-xl leading-[120%]",
                { "font-semibold": message !== MessageEnum.COURSE_START },
              )}
            >
              {getMessage(
                message,
                dayjs(startDate, INTERNAL_DATE_FORMAT).format("D MMMM YYYY"),
              )}
            </p>
          </div>

          <div className="flex w-[70%] p-2 3xl:pl-0 justify-between h-full flex-row gap-4">
            <Card key="one" className="flex-[1_1_155px] grid grid-cols-1 grid-rows-[32px_auto] py-4 px-6">
              {lessonsDone && lessonsDone > 0 ? (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={animation?.container}
                  className="text-2xl font-semibold text-secondary"
                  transition={{ duration: 1.0 }}
                >
                  {lessonsDone}
                </motion.div>
              ) : (
                <Dash className="my-auto" />
              )}

              <p className="text-xs leading-[150%]">
                <Trans
                  values={{
                    lessons_done: !noPercentageLessonsDone
                      ? percentageLessonsDone
                      : 0,
                  }}
                  i18nKey="home.course.lessons_done" // optional -> fallbacks to defaults if not provided
                  defaults="Lessons done <br/> <bold>{{lessons_done}}%</bold>" // optional defaultValue
                  components={{
                    br: <br />,
                    bold: (
                      <b className="font-semibold capitalize text-primary" />
                    ),
                  }}
                />
              </p>
            </Card>

            <Card key="two" className="flex-[1_1_155px] grid grid-cols-1 grid-rows-[32px_auto] py-4 px-6">
              {exercisesDone && exercisesDone > 0 ? (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={animation?.container}
                  className="text-2xl font-semibold text-secondary"
                  transition={{ duration: 1.0 }}
                >
                  {exercisesDone}

                  {Boolean(
                    recentlyDoneExercises && recentlyDoneExercises > 0,
                  ) && (
                    <motion.sup
                      initial="hidden"
                      animate="visible"
                      variants={animation?.item}
                      className="text-[#219653] text-[10px] align-super leading-[100%]"
                      transition={{ duration: 5.5 }}
                    >
                      +{recentlyDoneExercises}
                    </motion.sup>
                  )}
                </motion.div>
              ) : (
                <Dash className="my-auto" />
              )}

              <p className="text-xs leading-[150%]">
                <Trans
                  values={{
                    lessons_done: !noPercentageExercisesDone
                      ? percentageExecisesDone
                      : 0,
                  }}
                  i18nKey="home.course.exercises_done" // optional -> fallbacks to defaults if not provided
                  defaults="Exercises done <br/> <bold>{{lessons_done}}%</bold>" // optional defaultValue
                  components={{
                    br: <br />,
                    bold: (
                      <b className="font-semibold capitalize text-primary" />
                    ),
                  }}
                />
              </p>
            </Card>

            {!isOneToOneOnly && (
              <Card key="three" className="flex-[1_1_300px] flex flex-col gap-2 py-4 px-6">
                <div className="flex flex-row justify-between text-xs leading-[150%]">
                  <Trans
                    i18nKey="home.course.progress" // optional -> fallbacks to defaults if not provided
                    defaults="Your course progress" // optional defaultValue
                  />

                  <p className="text-primary font-semibold">
                    {!noProgress ? `${Math.ceil(progress ?? 0.0)}%` : "-"}
                  </p>
                </div>

                <Progress value={progress} size="sm" />

                <div className="flex flex-row items-center justify-between">
                  {startDate !== undefined && dayjs(startDate).isValid() && (
                    <div className="text-secondary bg-[--primary300] px-2 py-1 w-fit  font-medium text-xs rounded-sm flex justify-center items-center">
                      {t("home.date", {
                        day: `${new Date(startDate as string).getDate()}`,
                        month: `${t(
                          `months.short.${
                            new Date(startDate as string).getMonth() + 1
                          }`,
                        )}`,
                        year: `${new Date(startDate as string).getFullYear()}`,
                      })}
                    </div>
                  )}

                  {endDate !== undefined && dayjs(endDate).isValid() && (
                    <div className="text-secondary bg-[--primary300] px-2 py-1 w-fit capitalize font-medium text-xs rounded-sm flex flex-row gap-3 justify-center items-center">
                      <svg
                        fill="none"
                        width="11"
                        xmlns="http://www.w3.org/2000/svg"
                        height="11"
                        viewBox="0 0 11 11"
                      >
                        <path
                          d="M10.7302 0.920832C10.6502 0.885954 10.5615 0.873891 10.4745 0.886081C10.3876 0.898271 10.3061 0.934198 10.2399 0.989585C8.63594 2.32174 7.2772 1.67601 5.70249 0.927432C4.07108 0.15135 2.22142 -0.727036 0.1581 0.989585C0.1088 1.03062 0.0691932 1.08128 0.041928 1.1382C0.0146628 1.19511 0.000368142 1.25695 0 1.3196V10.56C0 10.6767 0.0482811 10.7886 0.134222 10.8711C0.220163 10.9536 0.336723 11 0.458262 11C0.5798 11 0.696361 10.9536 0.782302 10.8711C0.868242 10.7886 0.916523 10.6767 0.916523 10.56V8.12723C2.45113 6.96338 3.77321 7.59096 5.29579 8.31479C6.23522 8.76086 7.24684 9.24158 8.33177 9.24158C9.12972 9.24158 9.96662 8.98032 10.8419 8.25154C10.8912 8.2105 10.9308 8.15984 10.9581 8.10293C10.9853 8.04601 10.9996 7.98417 11 7.92152V1.3196C10.9995 1.2353 10.9739 1.1529 10.926 1.08221C10.8782 1.01151 10.8102 0.9555 10.7302 0.920832ZM0.916523 7.06404V4.50313C1.88174 3.8827 2.78623 3.86565 3.66609 4.09941V6.72522C2.79941 6.51566 1.88288 6.52886 0.916523 7.06404ZM10.0818 7.71361C9.1154 8.44624 8.23324 8.46825 7.33219 8.20368V5.58173C6.09374 5.25171 4.90455 4.42668 3.66609 4.09941V1.03744C4.19596 1.19309 4.73212 1.4472 5.29579 1.71507C5.94079 2.02143 6.61959 2.34374 7.33219 2.5159V5.58173C8.21148 5.81549 9.11597 5.79843 10.0818 5.17746V7.71361Z"
                          fill="#2D224C"
                        />
                      </svg>

                      {t("home.date", {
                        day: `${new Date(endDate as string).getDate()}`,
                        month: `${t(
                          `months.short.${
                            new Date(endDate as string).getMonth() + 1
                          }`,
                        )}`,
                        year: `${new Date(endDate as string).getFullYear()}`,
                      })}
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        <div className="flex flex-[1_1_calc(100%_-_110px)] flex-row w-full gap-3 h-full">
          <Notifications />

          {!isOneToOne && (
            <CourseStructure isOneToOne={isOneToOne} course={course as never} />
          )}
        </div>
      </div>
    </>
  );
};

export default DesktopView;
